import {
  RadioButtonGroupField,
  TextAreaField,
  TextInputField,
  SimpleModal,
  Flex
} from '@sequencehq/core-components'
import {
  isNumber,
  required,
  percentage,
  lessThanEqualTo,
  maxLength
} from '@sequencehq/utils/validation'
import {
  useForm,
  decimalFromPercentage,
  toCurrencySymbol
} from '@sequencehq/utils'
import { Currency } from '@sequencehq/api'
import { match } from 'ts-pattern'
import type { Dashboardv99990101CommonModels } from '@sequencehq/api/dashboard/v99990101'

type CreditNoteLineItemRateDisplay =
  Dashboardv99990101CommonModels.CreditNoteLineItems.CreditNoteLineItemRateDisplay
import {
  CREDIT_NOTE_LINE_ITEM_MAX_TEXT_LENGTH,
  MAX_INPUT_TOTAL,
  MAX_QUANTITY
} from 'CreditNotes/constants'
import useToggle from 'lib/hooks/useToggle'
import {
  RevRecFormValues,
  RevenueRecognitionMethodField,
  ServicePeriodField
} from './RevRecFields'
import { ProductField, SelectedProduct } from './ProductField'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useTaxIntegrationProvider } from 'lib/hooks/useTaxIntegrationProvider'
import { TaxCategoryField } from 'Invoices/components/CreateEditShared/LineItemEditor/common/fields'

const creditNoteLineItemRateDisplay = {
  ABSOLUTE: 'ABSOLUTE',
  PERCENTAGE: 'PERCENTAGE'
} as const satisfies Record<string, CreditNoteLineItemRateDisplay>

const creditNoteRateDisplayOptions = [
  {
    label: 'Fixed',
    value: creditNoteLineItemRateDisplay.ABSOLUTE
  },
  {
    label: 'Percentage',
    value: creditNoteLineItemRateDisplay.PERCENTAGE
  }
]

export type FormValues = {
  title: string
  description: string
  rateDisplay: CreditNoteLineItemRateDisplay
  quantity: string
  amount: string
  tax: string
  productId: string | undefined
  taxCategoryId: string
} & RevRecFormValues

export type SubmitValues = FormValues & {
  rate: string
  taxRate: string
}

const productRequired = (value: unknown): string | undefined =>
  value ? undefined : 'Please select a product'

// Anrok determines tax from either a product or a tax category, so one of the
// two must be selected.
const productOrTaxCategoryRequired = (
  value: unknown,
  allValues?: object
): string | undefined =>
  !value && !(allValues as FormValues)?.taxCategoryId
    ? 'Please select a product or a tax category'
    : undefined

const taxCategoryOrProductRequired = (
  value: unknown,
  allValues?: object
): string | undefined =>
  !value && !(allValues as FormValues)?.productId
    ? 'Please select a product or a tax category'
    : undefined

const resolveTaxRate = ({
  formTax,
  initialFormTax,
  initialTaxRate,
  isAnrok
}: {
  formTax: string
  initialFormTax: string
  initialTaxRate: string | undefined
  isAnrok: boolean
}): string => {
  // Send the stored rate as-is: rebuilding it from the percentage field rounds it
  // to 10 decimal places.
  const hasUntouchedStoredTaxRate =
    initialTaxRate !== undefined && formTax === initialFormTax
  if (hasUntouchedStoredTaxRate) {
    return initialTaxRate
  }
  // Anrok hides the field and fills in the rate itself; the API still needs one.
  if (isAnrok && formTax === '') {
    return '0'
  }
  return decimalFromPercentage(formTax)
}

interface Props {
  isOpen: boolean
  onClose: () => void
  onSubmit: (formValues: SubmitValues) => Promise<boolean>
  creditNoteCurrency: Currency
  initialValues: FormValues
  title: string
  submitButtonText: string
  /** Products hidden from the product select (already used by other line items). */
  excludedProductIds?: string[]
  /**
   * When true (the invoice-based "Issue and send" editor), a product must always
   * be selected and the tax category input is hidden — tax is derived from the
   * product. The standalone credit note editor leaves this false so users can
   * pick a tax category directly.
   */
  requireProduct?: boolean
  /** The line's stored tax rate, exactly as the API returned it. */
  initialTaxRate?: string
}

export const BaseCreditNoteLineItemDrawer = ({
  isOpen,
  onClose,
  onSubmit,
  creditNoteCurrency,
  initialValues,
  title,
  submitButtonText,
  excludedProductIds,
  requireProduct = false,
  initialTaxRate
}: Props) => {
  const [isFormValid, toggleIsFormValid] = useToggle(true)
  const flags = useFeatureFlags()
  const { isAnrok } = useTaxIntegrationProvider()

  // Tax category only appears (and tax is only derived from a product) under
  // Anrok, so the "product is mandatory, no tax category" behaviour only kicks
  // in there. Outside Anrok the manual tax % field still supplies the tax rate.
  const enforceProduct = requireProduct && isAnrok

  const productValidation = match({ enforceProduct, isAnrok })
    .with({ enforceProduct: true }, () => [productRequired])
    .with({ isAnrok: true }, () => [productOrTaxCategoryRequired])
    .otherwise(() => [])

  const taxCategoryValidation = match({ enforceProduct, isAnrok })
    .with({ enforceProduct: true }, () => [])
    .with({ isAnrok: true }, () => [taxCategoryOrProductRequired])
    .otherwise(() => [])

  const { fields, queries, mutators } = useForm<FormValues>({
    value: initialValues,
    showValidationErrors: !isFormValid,
    fieldConfiguration: [
      {
        property: 'title',
        validation: [
          required,
          maxLength(
            CREDIT_NOTE_LINE_ITEM_MAX_TEXT_LENGTH,
            `Name must not exceed ${CREDIT_NOTE_LINE_ITEM_MAX_TEXT_LENGTH} characters`
          )
        ]
      },
      {
        property: 'description',
        validation: [
          maxLength(
            CREDIT_NOTE_LINE_ITEM_MAX_TEXT_LENGTH,
            `Description must not exceed ${CREDIT_NOTE_LINE_ITEM_MAX_TEXT_LENGTH} characters`
          )
        ]
      },
      {
        property: 'rateDisplay',
        validation: []
      },
      {
        // Negative quantities/amounts are intentionally permitted; the only
        // guard is that the credit note's gross total stays >= 0, enforced at
        // issue time (see CreditNoteDetailsHeaderActions). isNumber keeps the
        // not-a-number check that isPositiveNumber previously provided.
        property: 'quantity',
        validation: [
          required,
          isNumber,
          (value: unknown) =>
            lessThanEqualTo(
              MAX_QUANTITY,
              `Amount can't be more than ${MAX_QUANTITY.toLocaleString()}`
            )(value)
        ]
      },
      {
        property: 'amount',
        validation: [
          required,
          isNumber,
          (value: unknown) =>
            lessThanEqualTo(
              MAX_INPUT_TOTAL,
              `Amount can't be more than ${MAX_INPUT_TOTAL.toLocaleString()}`
            )(value)
        ]
      },
      {
        property: 'tax',
        validation: isAnrok ? [] : [required, percentage]
      },
      {
        property: 'productId',
        validation: productValidation
      },
      {
        property: 'taxCategoryId',
        validation: taxCategoryValidation
      },
      {
        property: 'servicePeriod',
        validation: flags.useRevRec ? [required] : []
      },
      {
        property: 'revenueRecognitionMethod',
        validation: flags.useRevRec ? [required] : []
      }
    ]
  })

  const handleProductSelect = (product: SelectedProduct) => {
    const hasTitle = queries.formData.title.trim() !== ''

    mutators.updateFormData({
      productId: product.id,
      // When a product is selected, tax category is derived from the product on
      // the backend — clear the explicit selection so they don't conflict
      ...(isAnrok ? { taxCategoryId: '' } : {}),
      ...(hasTitle ? {} : { title: product.name })
    })
  }

  const rateDisplay = queries.formData.rateDisplay

  const handleSubmit = async (formValues: FormValues) => {
    const isAbsolute =
      formValues.rateDisplay === creditNoteLineItemRateDisplay.ABSOLUTE

    const taxRate = resolveTaxRate({
      formTax: formValues.tax,
      initialFormTax: initialValues.tax,
      initialTaxRate,
      isAnrok
    })

    // When Anrok is active the backend accepts exactly one of productId or
    // taxCategoryId. A product takes precedence — clear any stale taxCategoryId
    // that may have arrived via initialValues.
    const anrokTaxFields =
      isAnrok && formValues.productId ? { taxCategoryId: '' } : {}

    return onSubmit({
      ...formValues,
      ...anrokTaxFields,
      rate: isAbsolute
        ? formValues.amount
        : decimalFromPercentage(formValues.amount),
      taxRate
    })
  }

  const handleClose = () => {
    mutators.resetFormData()
    toggleIsFormValid(true)
    onClose()
  }

  return (
    <SimpleModal
      data-testid="creditNoteLineItemDrawer"
      isOpen={isOpen}
      onClose={handleClose}
      variant="drawer"
      title={title}
      cancelButtonText="Cancel"
      submitButtonText={submitButtonText}
      onSubmit={async () => {
        if (!queries.isValid) {
          toggleIsFormValid(false)
          return false
        }

        return handleSubmit(queries.formData)
      }}
    >
      <Flex flexDirection="column" gap="16px">
        <TextInputField
          label="Name"
          data-testid="creditNoteLineItemDrawer.name"
          value={fields.title.value}
          validationErrors={fields.title.validationErrors}
          onChange={event => fields.title.onChange(event.target.value)}
          placeholder="Line item name"
        />
        <TextAreaField
          label="Description"
          data-testid="creditNoteLineItemDrawer.description"
          value={fields.description.value}
          validationErrors={fields.description.validationErrors}
          onChange={fields.description.onChange}
          placeholder="Optional description"
        />
        <RadioButtonGroupField
          data-testid="creditNoteLineItemDrawer.rate"
          label="Rate"
          fullWidth
          value={fields.rateDisplay.value}
          options={creditNoteRateDisplayOptions}
          validationErrors={[]}
          onChange={value =>
            fields.rateDisplay.onChange(value as CreditNoteLineItemRateDisplay)
          }
        />
        {rateDisplay === creditNoteLineItemRateDisplay.ABSOLUTE && (
          <>
            <TextInputField
              label="Quantity"
              type="number"
              data-testid="creditNoteLineItemDrawer.quantity"
              value={fields.quantity.value}
              validationErrors={fields.quantity.validationErrors}
              onChange={event => fields.quantity.onChange(event.target.value)}
              placeholder="1"
            />
            <TextInputField
              label="Amount"
              type="number"
              data-testid="creditNoteLineItemDrawer.amount"
              prefix={toCurrencySymbol(creditNoteCurrency)}
              value={fields.amount.value}
              validationErrors={fields.amount.validationErrors}
              onChange={event => fields.amount.onChange(event.target.value)}
              placeholder="0.00"
            />
          </>
        )}

        {rateDisplay === creditNoteLineItemRateDisplay.PERCENTAGE && (
          <>
            <TextInputField
              label="Quantity"
              type="number"
              data-testid="creditNoteLineItemDrawer.quantity"
              prefix={toCurrencySymbol(creditNoteCurrency)}
              value={fields.quantity.value}
              validationErrors={fields.quantity.validationErrors}
              onChange={event => fields.quantity.onChange(event.target.value)}
              placeholder="0.00"
            />
            <TextInputField
              label="Amount"
              type="number"
              data-testid="creditNoteLineItemDrawer.amount"
              prefix="%"
              value={fields.amount.value}
              onChange={event => fields.amount.onChange(event.target.value)}
              validationErrors={
                parseFloat(fields.amount.value) > 100
                  ? [{ message: 'Please enter a number between 0 and 100' }]
                  : fields.amount.validationErrors
              }
              placeholder="0"
            />
          </>
        )}

        <ProductField
          productId={fields.productId.value}
          onChange={handleProductSelect}
          excludedProductIds={excludedProductIds}
          validationErrors={fields.productId.validationErrors}
        />

        {isAnrok ? (
          <>
            {!enforceProduct && !fields.productId.value && (
              <TaxCategoryField
                label="Tax category"
                data-testid="creditNoteLineItemDrawer.taxCategory"
                taxCategoryId={fields.taxCategoryId.value}
                setTaxCategoryId={fields.taxCategoryId.onChange}
                validationErrors={fields.taxCategoryId.validationErrors}
              />
            )}
          </>
        ) : (
          <TextInputField
            label="Tax"
            type="number"
            data-testid="creditNoteLineItemDrawer.tax"
            prefix="%"
            value={fields.tax.value}
            onChange={event => fields.tax.onChange(event.target.value)}
            validationErrors={fields.tax.validationErrors}
            placeholder="0"
          />
        )}

        <ServicePeriodField
          servicePeriod={fields.servicePeriod.value}
          onChange={fields.servicePeriod.onChange}
          validationErrors={fields.servicePeriod.validationErrors}
        />

        <RevenueRecognitionMethodField
          revenueRecognitionMethod={fields.revenueRecognitionMethod.value}
          onChange={fields.revenueRecognitionMethod.onChange}
          validationErrors={fields.revenueRecognitionMethod.validationErrors}
        />
      </Flex>
    </SimpleModal>
  )
}
